"use client";

import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { createClient } from "@/lib/supabase/client";
import {
  readCachedCampaign,
  refreshCampaignCache,
  SPLASH_SESSION_SHOWN_KEY,
  type SplashCampaign,
} from "@/lib/splash/campaign";

// 네이티브 스플래시(launchAutoHide 1500ms)에서 이어받아 총 ~2.1초 표시 후 페이드.
const DISPLAY_MS = 1800;
const FADE_MS = 300;
const REFRESH_DELAY_MS = 2500; // 부팅 크리티컬 작업이 끝난 뒤 백그라운드 갱신

type Phase = "visible" | "fading";

interface OverlayState {
  phase: Phase;
  campaign: SplashCampaign | null; // null = 기본 로고 스플래시
}

/**
 * 네이티브 앱 콜드 스타트 스플래시 오버레이 (2단계 스플래시의 2단계).
 *
 * 1단계인 네이티브 스플래시는 바이너리에 고정된 로고 화면이고, 이 컴포넌트가
 * 웹 첫 페인트에서 같은 모습(또는 캐시된 이벤트 캠페인)으로 이어받아
 * 네이티브 → 웹 전환의 깜빡임을 가린다. 캠페인은 super-admin에서 관리하며
 * 스토어 심사 없이 변경된다. 웹/PWA에서는 아무것도 렌더하지 않는다.
 */
export function NativeSplashOverlay() {
  const [state, setState] = useState<OverlayState | null>(null);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    // 세션 내 전체 리로드(에러 복구 등)에서 재표시 방지.
    // WKWebView 콜드 스타트는 항상 새 sessionStorage → 콜드 스타트엔 항상 표시.
    let alreadyShown = false;
    try {
      alreadyShown = !!sessionStorage.getItem(SPLASH_SESSION_SHOWN_KEY);
      if (!alreadyShown) sessionStorage.setItem(SPLASH_SESSION_SHOWN_KEY, "1");
    } catch {
      // sessionStorage 불가 시 표시하지 않음 (fail-open)
      alreadyShown = true;
    }

    if (!alreadyShown) {
      setState({ phase: "visible", campaign: readCachedCampaign() });

      // 오버레이가 페인트된 다음 프레임에 네이티브 스플래시 해제 → 이음새 없음.
      // launchAutoHide(1500ms)는 안전망으로 그대로 둔다 (웹 로드 실패 대비).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          SplashScreen.hide().catch(() => {});
        });
      });

      const reduceMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          if (reduceMotion) {
            setState(null);
          } else {
            setState((prev) => (prev ? { ...prev, phase: "fading" } : prev));
            timers.push(
              setTimeout(() => {
                if (!cancelled) setState(null);
              }, FADE_MS),
            );
          }
        }, DISPLAY_MS),
      );
    }

    // 다음 콜드 스타트용 캠페인 캐시 갱신 (표시 여부와 무관하게 네이티브에서 항상)
    timers.push(
      setTimeout(() => {
        if (!cancelled) refreshCampaignCache(createClient());
      }, REFRESH_DELAY_MS),
    );

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, []);

  if (!state) return null;

  return (
    <div
      aria-hidden="true"
      className={`fixed inset-0 z-[10000] transition-opacity duration-300 ${
        state.phase === "fading" ? "opacity-0" : "opacity-100"
      }`}
      style={{
        backgroundColor: state.campaign?.background_color ?? "#FFFFFF",
      }}
    >
      {state.campaign ? (
        // 프리로드된 URL과 byte-identical해야 HTTP 캐시가 맞으므로 next/image 금지
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={state.campaign.image_url}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center pt-safe pb-safe">
          {/* 네이티브 스플래시와 동일한 워드마크·비율(화면 폭 ~31%) → 심리스 전환.
              pt-safe/pb-safe: 네이티브 쪽이 safe area 기준 중앙이므로 여기도 동일 기준. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/locarora_bg.png" alt="" style={{ width: "31%" }} />
        </div>
      )}
    </div>
  );
}
